<template>
  <ToolShell
    tool-name="Software stack cost calculator"
    h1="What does your software stack really cost?"
    lede="Your software stack cost is what every tool you pay for adds up to each month, at the plan your team size actually needs. Tick the tools you pay for and set your seats to get the monthly and annual total, the flat Workspace369 plan that covers it, and the gaps it doesn't. Free, instant, no signup."
    :trust="['No signup', 'Prices with checked-on dates', 'Honest gap list included']"
    form-title="Your current tools"
    :upsell-points="[
      'One flat plan with seats included — no per-tool math',
      'Proposals, invoices, projects, inbox, and portal in one place',
      'Calls and texts start on Voyager, IVR on Specialist — tied to the client record',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'Adds up the stack you have today, once',
      'You still pay for — and juggle — every tool',
      'Nothing lands on one client record',
    ]"
    upgrade-headline="The math works once. One flat plan ends the juggling."
    :connected-bullets="[
      'One flat plan, seats included — CRM, projects, shared inbox, and client portal',
      'Proposals, invoices, and payments on the same client record',
      'Time tracking, AI, and automations built in, with accounting from Specialist',
      'Calls and texts start on Voyager ($74, 2 seats); IVR and call queues on Specialist ($149, 10 seats)',
    ]"
    slug="/tools/software-stack-cost-calculator"
    :meta-description="metaDescription"
    doc-brand="Stack cost breakdown"
    :doc-subtitle="docSubtitle"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <p class="stack-intro">Tick what you pay for today. Seats mean people who use that tool.</p>

      <div class="stack-tools">
        <div
          v-for="tool in tools"
          :key="tool.id"
          class="stack-tool"
          :class="{ 'stack-tool-on': sel[tool.id].checked }"
        >
          <label class="stack-tool-main">
            <input v-model="sel[tool.id].checked" type="checkbox" />
            <span class="stack-tool-name">{{ tool.name }}</span>
            <span class="stack-tool-hint">{{ entryHint(tool) }}</span>
          </label>
          <div v-if="sel[tool.id].checked" class="stack-tool-seats">
            <label :for="`seats-${tool.id}`">Seats</label>
            <input
              :id="`seats-${tool.id}`"
              v-model.number="sel[tool.id].seats"
              type="number"
              min="1"
              max="99"
              step="1"
              @change="sel[tool.id].seats = clampSeats(sel[tool.id].seats)"
            />
          </div>
        </div>
      </div>

      <p class="ta-note">
        Entry plans are assumed, matched to your seat count where the vendor publishes seat limits.
        Prices come from each vendor's pricing page — the checked dates sit under the breakdown.
      </p>
    </template>

    <template #preview>
      <div v-if="!lineItems.length" class="stack-empty">
        <p><strong>Your stack breakdown appears here.</strong></p>
        <p>
          Tick the tools you pay for on the left. You'll get the monthly and annual total, the flat
          Workspace369 plan that covers it, and — just as important — what that plan does
          <em>not</em> replace.
        </p>
      </div>

      <template v-else>
        <div class="ta-doc-meta">
          <div><span>Tools ticked</span><strong>{{ lineItems.length }}</strong></div>
          <div><span>Team size</span><strong>{{ teamSize }} {{ teamSize === 1 ? "seat" : "seats" }}</strong></div>
          <div><span>Stack / month</span><strong>{{ money(stackMonthly) }}</strong></div>
          <div><span>Stack / year</span><strong>{{ money(stackAnnual) }}</strong></div>
        </div>

        <div class="ta-table-scroll" tabindex="0">
          <table class="ta-table">
            <thead>
              <tr><th>Your stack today</th><th>How it adds up</th><th>Per month</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in lineItems" :key="item.id" :title="`Checked ${item.checkedLabel} · ${item.url}`">
                <td>{{ item.name }} — {{ item.planName }}</td>
                <td class="ta-num">{{ item.detail }}</td>
                <td class="ta-num">{{ item.seeSite ? "see site" : money(item.lineTotal) + (item.yearlyFlag ? "*" : "") }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="ta-doc-total">
          <span>Your stack / month{{ hasSeeSite ? " (known prices)" : "" }}</span>
          <strong>{{ money(stackMonthly) }}</strong>
        </div>

        <div class="stack-compare">
          <p class="stack-compare-kicker">The flat plan that covers this</p>
          <div class="stack-compare-row">
            <div class="stack-compare-us">
              <strong>Workspace369 {{ w369Plan.name }}</strong>
              <span>{{ money(w369Comparison.monthly) }}/mo · {{ w369Plan.seats }} {{ w369Plan.seats === 1 ? "seat" : "seats" }} included<template v-if="w369Comparison.extraSeats"> · {{ money(w369Plan.monthly) }} + {{ w369Comparison.extraSeats }} × {{ money(w369Comparison.extraSeatRate) }}</template></span>
            </div>
            <div class="stack-compare-delta">
              <template v-if="monthlyDelta > 0">
                <strong>{{ money(monthlyDelta) }} less / mo</strong>
                <span>{{ money(monthlyDelta * 12) }} stays with you each year</span>
              </template>
              <template v-else-if="monthlyDelta < 0">
                <strong>{{ money(-monthlyDelta) }} more / mo</strong>
                <span>Your stack is cheaper on paper — the gaps below still apply</span>
              </template>
              <template v-else>
                <strong>Same cost / mo</strong>
                <span>One login instead of {{ lineItems.length }}</span>
              </template>
            </div>
          </div>
          <p class="stack-compare-note">
            One flat plan, seats included: CRM, projects, shared inbox, proposals, invoices, payments,
            client portal, and AI. Calls and texts start on Voyager ($74, 2 seats); IVR and call queues on
            Specialist ($149, 10 seats).
            <template v-if="teamSize > 20">
              Teams over 20 fit Enterprise ($699/mo, 50 seats); extra seats are $10/seat/mo on Voyager
              and above.
            </template>
          </p>
          <div class="stack-compare-cta">
            <a
              :href="planUrl"
              class="stack-compare-btn"
              data-conversion-event="start_free_click"
              data-conversion-label="Stack calculator plan CTA"
              data-conversion-location="/tools/software-stack-cost-calculator"
            >
              Start free trial
            </a>
            <NuxtLink :to="localePath('/pricing')" class="stack-compare-link">See full pricing</NuxtLink>
          </div>
          <TrialDisclosure />
        </div>

        <div class="stack-gaps">
          <p class="stack-gaps-kicker">What Workspace369 does NOT replace</p>
          <ul>
            <li v-for="gap in gaps" :key="gap">{{ gap }}</li>
          </ul>
        </div>

        <p class="ta-doc-note">
          * Billed-yearly rate — the vendor's monthly rate wasn't on the page we checked. Annual =
          monthly × 12. Prices checked: {{ checkedSummary }}. Vendors change prices often, so confirm
          before you buy. Your picks stay in this browser tab; nothing is sent anywhere.
        </p>
      </template>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Tick your tools</h2><p>Every tool you pay for today — CRM, scheduler, phone line, time tracker, all of it.</p></div></div>
      <div><span>02</span><div><h2>Set your seats</h2><p>How many people use each tool. The math handles per-seat prices and flat plans.</p></div></div>
      <div><span>03</span><div><h2>Read the real cost</h2><p>Your stack's monthly and annual cost, the flat plan that covers it, and the gaps it won't.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Stack cost questions" :faq="faq" />
  </ToolShell>
</template>

<script setup>
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import toolPrices from "~/data/stack-tool-prices.json";
import pricingFeatures from "~/data/pricing-features.json";
import { stackComparisonCost } from "~/utils/stack-comparison-cost.mjs";

const tools = toolPrices.tools;

const sel = reactive(
  Object.fromEntries(tools.map((tool) => [tool.id, { checked: false, seats: 1 }])),
);

const { localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();

const money = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const clampSeats = (value) => Math.min(99, Math.max(1, Math.round(Number(value) || 1)));

// A plan's usable price: monthly billing preferred, yearly rate as fallback.
const effectivePrice = (plan) =>
  typeof plan.monthly === "number"
    ? plan.monthly
    : typeof plan.annualMonthly === "number"
      ? plan.annualMonthly
      : null;

// Cheapest plan that fits the seat count (null = no published fit or no published price).
const pickPlan = (tool, seats) => {
  const fits = tool.plans.filter((plan) => plan.maxUsers == null || plan.maxUsers >= seats);
  let best = null;
  for (const plan of fits) {
    const price = effectivePrice(plan);
    if (price == null) continue;
    if (!best || price < best.price) best = { plan, price };
  }
  return best;
};

const entryHint = (tool) => {
  const prices = tool.plans.map(effectivePrice).filter((price) => typeof price === "number");
  if (!prices.length) return "see site";
  const paid = prices.filter((price) => price > 0);
  const unit = tool.perSeat ? "/seat" : "";
  if (prices.includes(0) && paid.length) return `Free, or from $${Math.min(...paid)}${unit}/mo`;
  return `from $${Math.min(...prices)}${unit}/mo`;
};

const lineItems = computed(() =>
  tools
    .filter((tool) => sel[tool.id].checked)
    .map((tool) => {
      const seats = clampSeats(sel[tool.id].seats);
      const picked = pickPlan(tool, seats);
      if (!picked) {
        return {
          id: tool.id,
          name: tool.name,
          planName: "plan fit unknown",
          detail: tool.perSeat ? `${seats} seats` : "flat",
          lineTotal: null,
          seeSite: true,
          yearlyFlag: false,
          checkedLabel: fmtDate(tool.checkedOn),
          url: tool.url,
        };
      }
      const { plan, price } = picked;
      return {
        id: tool.id,
        name: tool.name,
        planName: plan.name,
        detail: tool.perSeat
          ? `${seats} × ${money(price)}`
          : plan.maxUsers
            ? `flat · up to ${plan.maxUsers} ${plan.maxUsers === 1 ? "user" : "users"}`
            : "flat",
        lineTotal: price * (tool.perSeat ? seats : 1),
        seeSite: false,
        yearlyFlag: typeof plan.monthly !== "number" && typeof plan.annualMonthly === "number",
        checkedLabel: fmtDate(tool.checkedOn),
        url: tool.url,
      };
    }),
);

const stackMonthly = computed(() =>
  lineItems.value.reduce((sum, item) => sum + (item.lineTotal || 0), 0),
);
const stackAnnual = computed(() => stackMonthly.value * 12);
const hasSeeSite = computed(() => lineItems.value.some((item) => item.seeSite));

const teamSize = computed(() =>
  lineItems.value.length
    ? Math.max(...tools.filter((t) => sel[t.id].checked).map((t) => clampSeats(sel[t.id].seats)))
    : 1,
);

const w369Comparison = computed(
  () => stackComparisonCost(pricingFeatures, teamSize.value, stackMonthly.value),
);
const w369Plan = computed(() => w369Comparison.value.plan);

const monthlyDelta = computed(() => w369Comparison.value.monthlyDelta);

const planUrl = computed(() =>
  addReferralToUrl(`https://app.workspace369.com/signup?plan=${w369Plan.value.slug}`),
);

// Neutral capability gaps, shown only for the tools the user ticked.
const gaps = computed(() => {
  const has = (id) => sel[id] && sel[id].checked;
  const list = [];
  if (has("honeybook")) {
    list.push(
      "HoneyBook-style smart files: one document that combines the proposal, contract and payment. Workspace369 has proposals with a deposit on acceptance, and contracts with e-signature, but no direct smart-file equivalent today.",
    );
  }
  if (has("quickbooks")) {
    list.push(
      "Advanced bookkeeping beyond Workspace369's basic (Specialist) and full (Commander) accounting tiers — plus payroll and tax filing, which Workspace369 does not do. If your accountant runs on QuickBooks, keep it.",
    );
  }
  if (has("hubspot")) {
    list.push(
      "Marketing automation at HubSpot's Professional and Enterprise level: ad tools, attribution and marketing campaigns. Workspace369's automations cover client email and SMS follow-up, not ads or attribution.",
    );
  }
  if (has("google-voice") || has("quo")) {
    list.push(
      "Published phone coverage — Quo and Google Voice publish their supported countries. Workspace369 does not publish a country list for SMS/voice, so confirm your number and coverage before you switch.",
    );
  }
  list.push(
    "A one-click import. AI Import brings clients, services, invoices, proposals and expenses (Specialist and up) in from CSV or Excel exports, and you review every row before it saves. Projects, tasks, contracts, files, messages and payment history don't move, so plan to re-create what you still need.",
  );
  return list;
});

const checkedSummary = computed(() => {
  const groups = new Map();
  for (const tool of tools) {
    const label = fmtDate(tool.checkedOn);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(tool.name);
  }
  return [...groups.entries()]
    .map(([label, names]) => `${names.length > 2 ? "all other tools" : names.join(" and ")} ${label}`)
    .join(" · ");
});

const docSubtitle = computed(() =>
  lineItems.value.length
    ? `${lineItems.value.length} tools · ${teamSize.value} ${teamSize.value === 1 ? "seat" : "seats"}`
    : "Tick tools to build the breakdown",
);

const guideSections = [
  {
    title: "How the calculator adds up your stack",
    paragraphs: [
      "For each tool you tick, the calculator picks the cheapest published plan that allows your seat count. Per-seat tools are multiplied by your seats; flat-priced plans count once. Prices come from each vendor's own pricing page, and the date each was checked is listed under the breakdown.",
    ],
    bullets: [
      "Monthly billing is used where the vendor publishes it. Where only a billed-yearly rate is shown, the line carries an asterisk.",
      "If no published plan fits your seats, the line says see site and is left out of the total rather than guessed.",
      "Your team size is the highest seat count across the tools you ticked.",
      "The Workspace369 plan shown is the first one whose included seats cover that team size.",
    ],
  },
  {
    title: "Matching your team to a Workspace369 plan",
    paragraphs: [
      "Workspace369 plans include 1, 2, 10, 20 or 50 seats, from Cadet to Enterprise. Voyager and above also take extra seats at $10 a month each, so a team just over a plan's included seats can compare adding a seat or two with moving up a plan.",
      "Seats are not the only difference between plans. Calls and texts start on Voyager, and the phone system with IVR and call queues starts on Specialist, so check the plan comparison for the features you rely on.",
    ],
    links: [
      { label: "Full plan comparison", path: "/pricing" },
      { label: "Client-work software price tracker", path: "/research/client-work-software-price-tracker" },
    ],
  },
  {
    title: "Costs a price list does not show",
    paragraphs: [
      "The total above is what the vendors charge for the plans that fit. A few costs sit outside it, and they are worth a line in your own notes.",
    ],
    bullets: [
      "Add-ons and usage charges, such as extra phone numbers, call minutes or storage.",
      "Annual contracts that renew on a date you have to remember, sometimes at a higher price.",
      "Tools paid on someone's own card that never reach the business accounts.",
      "Time spent copying the same client details between tools, and fixing the copies that drift apart.",
    ],
  },
  {
    title: "Before you cancel a tool",
    paragraphs: [
      "Switching only saves money once the old subscription is gone, and it only works if nothing is lost on the way.",
    ],
    bullets: [
      "Check each tool's renewal date and notice period, so you cancel before the next charge.",
      "Export your data first: clients, invoices, proposals and anything you are required to keep.",
      "Run the new setup next to the old one for one billing cycle, then cancel.",
      "Tell clients if the way they pay, book or message you is changing.",
    ],
    links: [
      { label: "Switching from HoneyBook", path: "/switch/honeybook" },
      { label: "Alternatives to popular tools", path: "/alternatives" },
    ],
  },
];

const faq = [
  {
    q: "How accurate are the prices?",
    a: "Each price comes from the vendor's own pricing page, and the date it was checked is shown under the breakdown. Vendors change prices and plans often, so confirm on their site before you buy or cancel anything.",
  },
  {
    q: "Why does the calculator use the cheapest plan that fits?",
    a: "It shows the lowest you could be paying for your seat count, so the comparison is fair to your current tools. If you pay for a higher tier for its features, your real stack costs more than the total shown.",
  },
  {
    q: "Does Workspace369 replace every tool on the list?",
    a: "Not always. The breakdown lists what Workspace369 does not replace for the tools you ticked, such as payroll and tax filing, or HoneyBook-style smart files. Keep a tool when it does something you need that Workspace369 does not.",
  },
  {
    q: "Is anything I tick saved or sent?",
    a: "No. Your picks stay in this browser tab, and nothing is sent anywhere.",
  },
];

const metaDescription =
  "Add up what your current tools really cost per month — and see the flat plan that replaces the stack. Free, instant, no signup.";

useHead(() => ({
  title: "Software Stack Cost Calculator | Workspace369",
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: "Software Stack Cost Calculator | Workspace369" },
    {
      name: "twitter:description",
      content:
        "Tick the tools you pay for, set seats, and see the real monthly cost — plus the flat plan that replaces the stack. Free, no signup.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Software Stack Cost Calculator | Workspace369" },
    {
      property: "og:description",
      content:
        "Tick the tools you pay for, set seats, and see the real monthly cost — plus the flat plan that replaces the stack. Free, no signup.",
    },
  ],
}));
</script>

<style scoped>
.stack-intro {
  margin-bottom: 0.9rem;
  color: #4b5563;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.55;
}

.stack-tools {
  display: grid;
  gap: 0.45rem;
  margin-bottom: 0.9rem;
}

.stack-tool {
  border: 1.5px solid #dde1e6;
  border-radius: 0.6rem;
  padding: 0.5rem 0.7rem;
  transition: border-color 150ms ease, background-color 150ms ease;
}

.stack-tool-on {
  border-color: rgb(14 116 144 / 0.5);
  background: rgb(14 116 144 / 0.04);
}

.stack-tool-main {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
}

.stack-tool-main input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  accent-color: #0e7490;
  cursor: pointer;
}

.stack-tool-name {
  color: #111827;
  font-size: 0.9rem;
  font-weight: 700;
}

.stack-tool-hint {
  margin-left: auto;
  color: #6b7280;
  font-size: 0.73rem;
  font-weight: 600;
  white-space: nowrap;
}

.stack-tool-seats {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.45rem 0 0.1rem 1.55rem;
}

.stack-tool-seats label {
  color: #4b5563;
  font-size: 0.76rem;
  font-weight: 700;
}

.stack-tool-seats input {
  width: 4rem;
  border: 1.5px solid #dde1e6;
  border-radius: 0.45rem;
  background: #fff;
  padding: 0.28rem 0.5rem;
  color: #111827;
  font-size: 0.86rem;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.stack-tool-seats input:focus {
  outline: none;
  border-color: #0e7490;
  box-shadow: 0 0 0 3px rgb(14 116 144 / 0.11);
}

/* empty state */
.stack-empty {
  padding: 1.6rem 1.7rem 2.2rem;
  color: #4b5563;
  font-size: 0.92rem;
  line-height: 1.7;
}

.stack-empty strong { color: #111827; }
.stack-empty p + p { margin-top: 0.5rem; }

/* comparison band */
.stack-compare {
  margin: 1.1rem 1.7rem 0;
  border: 1.5px solid rgb(14 116 144 / 0.35);
  border-radius: 10px;
  background: linear-gradient(160deg, #f0f9fc 0%, #f9fdff 100%);
  padding: 1rem 1.15rem;
}

.stack-compare-kicker {
  color: #111827;
  font-size: 0.86rem;
  font-weight: 700;
}

.stack-compare-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.6rem 1.5rem;
  margin-top: 0.55rem;
}

.stack-compare-us strong,
.stack-compare-delta strong {
  display: block;
  font-size: 1.02rem;
  font-weight: 900;
}

.stack-compare-us span,
.stack-compare-delta span {
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 600;
}

.stack-compare-delta { text-align: right; }
.stack-compare-delta strong { color: #0e7490; }

.stack-compare-note {
  margin-top: 0.7rem;
  color: #4b5563;
  font-size: 0.8rem;
  line-height: 1.6;
}

.stack-compare-cta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem 0.9rem;
  margin-top: 0.8rem;
}

.stack-compare-btn {
  display: inline-flex;
  align-items: center;
  min-height: 2.5rem;
  border-radius: 0.55rem;
  background: #0e7490;
  color: #fff;
  padding: 0.55rem 1.15rem;
  font-size: 0.86rem;
  font-weight: 800;
  box-shadow: 0 8px 18px -6px rgb(14 116 144 / 0.35);
  transition: background-color 150ms ease;
}

.stack-compare-btn:hover { background: #155e75; }

.stack-compare-link {
  color: #0e7490;
  font-size: 0.84rem;
  font-weight: 800;
}

.stack-compare-link:hover { text-decoration: underline; }

/* honesty block */
.stack-gaps {
  margin: 1.1rem 1.7rem 0;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 1rem 1.15rem;
}

.stack-gaps-kicker {
  color: #111827;
  font-size: 0.86rem;
  font-weight: 700;
}

.stack-gaps ul {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.6rem;
}

.stack-gaps li {
  position: relative;
  padding-left: 0.95rem;
  color: #4b5563;
  font-size: 0.82rem;
  line-height: 1.6;
}

.stack-gaps li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 999px;
  background: #9aa3ad;
}

@media (max-width: 480px) {
  .stack-compare-delta { text-align: left; }
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .stack-intro {
  color: #cbd5e1;
}

.dark .stack-tool {
  border: 1.5px solid #243247;
}

.dark .stack-tool-name {
  color: #f1f5f9;
}

.dark .stack-tool-hint {
  color: #cbd5e1;
}

.dark .stack-tool-seats label {
  color: #cbd5e1;
}

.dark .stack-tool-seats input {
  border: 1.5px solid #243247;
  background: #0f172a;
  color: #f1f5f9;
}

.dark .stack-empty {
  color: #cbd5e1;
}

.dark .stack-empty strong {
  color: #f1f5f9;
}

.dark .stack-compare {
  background: linear-gradient(160deg, rgb(14 116 144 / 0.16) 0%, #0f172a 100%);
}

.dark .stack-compare-kicker {
  color: #f1f5f9;
}

.dark .stack-compare-us span,
.dark .stack-compare-delta span {
  color: #cbd5e1;
}

.dark .stack-compare-delta strong {
  color: #67e8f9;
}

.dark .stack-compare-note {
  color: #cbd5e1;
}

.dark .stack-compare-link {
  color: #67e8f9;
}

.dark .stack-gaps {
  border: 1px dashed #243247;
}

.dark .stack-gaps-kicker {
  color: #f1f5f9;
}

.dark .stack-gaps li {
  color: #cbd5e1;
}


</style>
